import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import {
  ShoppingBag, ArrowLeft, Loader2, Minus, Plus, LayoutGrid, Search, X, Crown,
  Sparkles, ShoppingCart,
  Sofa, Wind, Archive, Lamp, PaintRoller, ShowerHead, Wifi, ShieldAlert,
  Sprout, Waves, TreePalm, Bug, SprayCan, Wrench, ChefHat, Hammer,
  PawPrint, Droplets, HardHat,
} from 'lucide-react';
import { api, thumb } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { trackViewContent, trackSearch } from '@/lib/analytics';
import { toast } from 'sonner';

// Map each shop category to a lucide icon for premium browsing.
// This map MUST stay in sync with /app/backend/shop_categories.py → CANONICAL_CATEGORIES.
const CATEGORY_ICONS = {
  'all': LayoutGrid,
  'Home Accents': Sofa,
  'Air Quality': Wind,
  'Storage & Organization': Archive,
  'Outdoor Lighting': Lamp,
  'Painting Supplies': PaintRoller,
  'Bath Fixtures': ShowerHead,
  'Smart Home': Wifi,
  'Hurricane & Emergency': ShieldAlert,
  'Landscaping': Sprout,
  'Pool & Spa': Waves,
  'Pool Maintenance': Droplets,
  'Patio & Deck': TreePalm,
  'Outdoor Essentials': Bug,
  'Cleaning Supplies': SprayCan,
  'Tools & Hardware': Wrench,
  'Kitchen & Bar': ChefHat,
  'Pet & Home': PawPrint,
  'Building Materials': HardHat,
};
const iconFor = (cat) => CATEGORY_ICONS[cat] || Hammer;

export function ShopPage() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(36);
  const { add } = useCart();
  const nav = useNavigate();

  const quickAdd = (e, p) => {
    // The whole card is a Link — keep the click from navigating
    e.preventDefault();
    e.stopPropagation();
    if (p.has_variants) {
      // Needs an option choice — send them to the detail page
      toast.info('Choose an option first', { description: p.name });
      nav(`/shop/${p.id}`);
      return;
    }
    add(p, 1);
  };

  useEffect(() => {
    api.get('/shop/products').then(r => setProducts(r.data.products || [])).finally(() => setLoading(false));
  }, []);

  // Reset the render window whenever the filter changes
  useEffect(() => { setVisible(36); }, [category, query]);

  const cats = useMemo(() => ['all', ...Array.from(new Set(products.map(p => p.category)))], [products]);

  const list = useMemo(() => {
    let filtered = category === 'all' ? products : products.filter(p => p.category === category);
    const q = query.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(p => {
        const hay = `${p.name || ''} ${p.category || ''}`.toLowerCase();
        // Every whitespace-separated term must appear somewhere
        return q.split(/\s+/).every(term => hay.includes(term));
      });
    }
    return filtered;
  }, [products, category, query]);

  // Only mount the cards in the current window — keeps the DOM light & scrolling smooth
  const shown = useMemo(() => list.slice(0, visible), [list, visible]);

  // Meta Pixel Search event — debounced so we fire once per completed query,
  // not per keystroke. Result count is read via ref to avoid effect refires.
  const resultCountRef = React.useRef(0);
  resultCountRef.current = list.length;
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => trackSearch(q, resultCountRef.current), 900);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <Layout>
      <PageHeader
        eyebrow="Shop"
        title="Curated home improvement essentials"
        subtitle="Hand-picked tools, materials, and finishing touches from the same suppliers our crews use every day."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-skeleton">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-[#E6E9EE] bg-white overflow-hidden">
                <div className="aspect-square bg-[#F2F7F5] animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-2.5 w-1/3 bg-[#F2F7F5] rounded animate-pulse" />
                  <div className="h-3.5 w-5/6 bg-[#F2F7F5] rounded animate-pulse" />
                  <div className="h-4 w-1/4 bg-[#F2F7F5] rounded animate-pulse mt-1" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <ShoppingBag className="h-10 w-10 mx-auto text-slate-300" />
            <div className="font-headline text-2xl text-[#17352D] mt-3">The shop is coming soon</div>
            <p className="mt-2 max-w-md mx-auto">We are curating our first product lineup. In the meantime, request a free estimate for any project and we will source everything for you.</p>
            <Button asChild className="mt-6 rounded-xl bg-[#17352D] text-white"><Link to="/estimate">Get Free Estimate</Link></Button>
          </div>
        ) : (
          <>
            {/* Search bar */}
            <div className="mb-4" data-testid="shop-search">
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${products.length} products — try "solar", "fan", "ceiling"…`}
                  data-testid="shop-search-input"
                  className="w-full h-12 pl-11 pr-11 rounded-2xl border border-[#E6E9EE] bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#17352D] focus:ring-2 focus:ring-[#17352D]/10 transition-colors-only"
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    data-testid="shop-search-clear"
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F2F7F5] text-slate-500 hover:text-[#17352D]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {(query || category !== 'all') && (
                <div className="mt-2 text-center text-xs text-slate-500" data-testid="shop-results-count">
                  {list.length === 0
                    ? 'No products match your search'
                    : `${list.length} ${list.length === 1 ? 'product' : 'products'} found${query ? ` for "${query}"` : ''}${category !== 'all' ? ` in ${category}` : ''}`}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5 mb-6" data-testid="shop-category-tabs">
              {cats.map(c => {
                const Icon = iconFor(c);
                const active = category === c;
                const label = c === 'all' ? 'All' : c;
                return (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`shop-filter-${c.toLowerCase().replace(/\s+/g, '-').replace(/&/g,'and')}`}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors-only max-w-[180px] ${
                      active
                        ? 'border-[#17352D] bg-[#17352D] text-white shadow-sm'
                        : 'border-[#E6E9EE] text-slate-700 hover:bg-[#F2F7F5] hover:border-[#17352D]/30'
                    }`}
                  >
                    <Icon className={`h-3 w-3 flex-shrink-0 ${active ? 'text-[#C8922F]' : 'text-slate-400'}`} />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {list.length === 0 ? (
                <div className="col-span-full py-16 text-center text-slate-500" data-testid="shop-empty-search">
                  <Search className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="mt-3 font-headline text-xl text-[#17352D]">No matches found</div>
                  <p className="mt-1 text-sm">Try a broader term or clear the search to see everything.</p>
                  {query && (
                    <button onClick={() => setQuery('')} className="mt-4 text-sm text-[#17352D] underline hover:text-[#C8922F]">Clear search</button>
                  )}
                </div>
              ) : shown.map(p => (
                <Link key={p.id} to={`/shop/${p.id}`} data-testid={`shop-card-${p.id}`}
                  className="group rounded-2xl border border-[#E6E9EE] bg-white overflow-hidden card-hover relative">
                  {p.is_cheapest && (
                    <div
                      data-testid={`cheapest-ribbon-${p.id}`}
                      className="absolute top-3 left-0 z-10 flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md rounded-r-full"
                      title="Beats every tracked competitor"
                    >
                      <Crown className="h-3 w-3" /> Cheapest in Market
                    </div>
                  )}
                  <div className="aspect-square overflow-hidden bg-[#F2F7F5] relative">
                    {p.image ? (
                      <img
                        src={thumb(p.image, 400)}
                        alt={p.name}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform-only"
                        onError={(e) => {
                          // Try original, then first gallery fallback, then hide broken img
                          const current = e.currentTarget.src;
                          if (p.image && current !== p.image && !e.currentTarget.dataset.triedOrig) {
                            e.currentTarget.dataset.triedOrig = '1';
                            e.currentTarget.src = p.image;
                            return;
                          }
                          const gallery = p.gallery || [];
                          const next = gallery.find(g => g && g !== current);
                          if (next) {
                            e.currentTarget.src = next;
                          } else {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentNode.classList.add('img-fallback');
                          }
                        }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-300"><ShoppingBag className="h-10 w-10" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{p.category}</div>
                    <div className="font-semibold text-[#17352D] line-clamp-2 mt-0.5">{p.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-headline text-lg text-[#17352D]">${Number(p.price).toFixed(2)}</span>
                      <span className="text-[10px] text-slate-500">{p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}</span>
                    </div>
                    <button
                      onClick={(e) => quickAdd(e, p)}
                      disabled={p.stock <= 0}
                      data-testid={`quick-add-${p.id}`}
                      aria-label={`Add ${p.name} to cart`}
                      className="mt-2.5 w-full h-9 rounded-xl bg-[#17352D] hover:bg-[#1E4338] disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors-only"
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                      {p.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                    </button>
                  </div>
                </Link>
              ))}
            </div>
            {shown.length < list.length && (
              <div className="mt-8 text-center">
                <Button
                  onClick={() => setVisible(v => v + 36)}
                  variant="outline"
                  className="rounded-xl h-11 px-8 border-[#17352D] text-[#17352D] hover:bg-[#F2F7F5]"
                  data-testid="shop-load-more"
                >
                  Show more ({list.length - shown.length} remaining)
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}

export function ProductDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/shop/products/${id}`)
      .then(r => {
        const p = r.data.product;
        setProduct(p);
        trackViewContent(p); // Meta Pixel ViewContent with catalog id (sku)
        if (p.has_variants && p.variants?.length) {
          setSelectedVariant(p.variants[0]);
        }
      })
      .catch(() => toast.error('Product not found'));
    // Frequently bought together — cached server-side for 7 days.
    setRelated([]);
    api.get(`/shop/products/${id}/related`, { params: { limit: 4 } })
      .then(r => setRelated(r.data.products || []))
      .catch(() => setRelated([]));
  }, [id]);

  // SEO: title, meta description, canonical + Product JSON-LD for Google
  useEffect(() => {
    if (!product) return;
    const prevTitle = document.title;
    document.title = product.seo_title || `${product.name} | Royal Palm Shop`;
    const ensure = (sel, create) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const meta = ensure('meta[name="description"]', () => {
      const m = document.createElement('meta'); m.name = 'description'; return m;
    });
    const prevDesc = meta.content;
    meta.content = product.meta_description
      || `${product.name} — $${Number(product.price).toFixed(2)} at Royal Palm Shop. Fast US shipping.`;
    const canonical = ensure('link[rel="canonical"]', () => {
      const l = document.createElement('link'); l.rel = 'canonical'; return l;
    });
    canonical.href = `${window.location.origin}/shop/product/${product.slug || product.id}`;
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'product-jsonld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'Product',
      name: product.name,
      image: product.images?.length ? product.images : [product.image].filter(Boolean),
      description: product.meta_description || product.description?.slice(0, 200),
      sku: product.sku || product.id,
      brand: { '@type': 'Brand', name: 'Royal Palm Shop' },
      offers: {
        '@type': 'Offer',
        url: `${window.location.origin}/shop/product/${product.slug || product.id}`,
        priceCurrency: 'USD',
        price: Number(product.price).toFixed(2),
        availability: (product.stock ?? 1) > 0
          ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      },
    });
    document.getElementById('product-jsonld')?.remove();
    document.head.appendChild(ld);
    return () => {
      document.title = prevTitle;
      meta.content = prevDesc || '';
      document.getElementById('product-jsonld')?.remove();
    };
  }, [product]);

  // Build image list (main + gallery) fallback if `images` array is empty
  const images = useMemo(() => {
    if (!product) return [];
    if (product.images?.length) return product.images;
    const arr = [];
    if (product.image) arr.push(product.image);
    if (product.gallery?.length) arr.push(...product.gallery);
    return arr;
  }, [product]);

  const activePrice = selectedVariant?.price ?? product?.price ?? 0;
  const activeStock = selectedVariant?.stock ?? product?.stock ?? 0;

  // When user picks a variant, prefer its image if present
  useEffect(() => {
    if (selectedVariant?.image) {
      const idx = images.indexOf(selectedVariant.image);
      if (idx >= 0) setActiveImage(idx);
    }
  }, [selectedVariant, images]);

  const buy = async () => {
    setLoading(true);
    try {
      const body = { purpose: 'product', product_id: id, quantity: qty };
      if (selectedVariant?.vid) body.variant_vid = selectedVariant.vid;
      const r = await api.post('/stripe/checkout', body);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  if (!product) return <Layout><div className="p-6 text-slate-500 text-center py-20"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div></Layout>;

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="product-detail-page">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#17352D]"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Image gallery */}
          <div>
            <div className="rounded-3xl overflow-hidden border border-[#E6E9EE] bg-[#F2F7F5] aspect-square flex items-center justify-center" data-testid="product-main-image">
              {images[activeImage] ? (
                <img
                  src={thumb(images[activeImage], 1000)}
                  alt={product.name}
                  decoding="async"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // First retry: fall back from proxy thumb to the original URL
                    const orig = images[activeImage];
                    if (orig && e.currentTarget.src !== orig && !e.currentTarget.dataset.triedOrig) {
                      e.currentTarget.dataset.triedOrig = '1';
                      e.currentTarget.src = orig;
                      return;
                    }
                    // Cascade to next valid image, else swap in placeholder
                    const parent = e.currentTarget.parentNode;
                    e.currentTarget.style.display = 'none';
                    // Try next image if any
                    const next = images.slice(activeImage + 1).find((u, k) =>
                      u && u !== images[activeImage] && !e.currentTarget.dataset.tried?.includes(String(activeImage + 1 + k))
                    );
                    if (next) { e.currentTarget.dataset.tried = (e.currentTarget.dataset.tried || '') + `,${activeImage + 1}`; setActiveImage(images.indexOf(next)); return; }
                    // Otherwise inject placeholder
                    if (parent && !parent.querySelector('.rp-img-fallback')) {
                      const div = document.createElement('div');
                      div.className = 'rp-img-fallback h-full w-full flex items-center justify-center text-slate-300';
                      div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2h12l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M6 8h12M6 12h12M6 16h12"/></svg>';
                      parent.appendChild(div);
                    }
                  }}
                />
              ) : (
                <div className="text-slate-300"><ShoppingBag className="h-16 w-16" /></div>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-6 gap-2" data-testid="product-image-thumbs">
                {images.slice(0, 12).map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`aspect-square rounded-lg overflow-hidden border-2 bg-[#F2F7F5] transition-colors-only ${activeImage === i ? 'border-[#C8922F] ring-2 ring-[#C8922F]/20' : 'border-[#E6E9EE] hover:border-[#17352D]/30'}`}
                  >
                    <img src={thumb(src, 200)} alt={`${product.name} ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={(e) => { if (src && e.currentTarget.src !== src) { e.currentTarget.src = src; return; } const btn = e.currentTarget.closest('button'); if (btn) btn.style.display = 'none'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#C8922F] font-semibold">{product.category}</div>
              {product.is_cheapest && (
                <div
                  data-testid="cheapest-badge"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold"
                  title="Beats every tracked competitor on Amazon, Walmart, and Home Depot"
                >
                  <Crown className="h-3 w-3" /> Cheapest in Market
                </div>
              )}
            </div>
            <h1 className="font-headline text-3xl md:text-4xl text-[#17352D] mt-2">{product.name}</h1>
            <div className="mt-3 font-headline text-3xl text-[#17352D]">${Number(activePrice).toFixed(2)}</div>
            <div className="text-sm text-slate-500 mt-1">{activeStock > 0 ? 'In stock' : 'Out of stock'}</div>

            {/* Variant selector */}
            {product.has_variants && product.variants?.length > 1 && (
              <div className="mt-5" data-testid="product-variant-selector">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Choose an option</div>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => {
                    const active = selectedVariant?.vid === v.vid;
                    const label = v.name || 'Default';
                    return (
                      <button
                        key={v.vid}
                        onClick={() => setSelectedVariant(v)}
                        data-testid={`variant-option-${v.vid}`}
                        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors-only ${
                          active
                            ? 'border-[#17352D] bg-[#17352D] text-white shadow-sm'
                            : 'border-[#E6E9EE] text-slate-700 hover:bg-[#F2F7F5] hover:border-[#17352D]/30'
                        }`}
                      >
                        {v.image && <img src={thumb(v.image, 100)} alt="" loading="lazy" className="h-8 w-8 rounded-md object-cover -ml-1" onError={(e) => { if (v.image && e.currentTarget.src !== v.image) e.currentTarget.src = v.image; }} />}
                        <span className="max-w-[200px] truncate">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.description && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">About this product</div>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{product.description}</p>
              </div>
            )}

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center border border-[#E6E9EE] rounded-xl overflow-hidden">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-12 w-12 flex items-center justify-center hover:bg-[#F2F7F5]" data-testid="shop-qty-minus"><Minus className="h-4 w-4" /></button>
                <div className="h-12 min-w-[3ch] px-3 flex items-center justify-center font-semibold text-[#17352D]">{qty}</div>
                <button onClick={() => setQty(qty + 1)} className="h-12 w-12 flex items-center justify-center hover:bg-[#F2F7F5]" data-testid="shop-qty-plus"><Plus className="h-4 w-4" /></button>
              </div>
              <Button
                onClick={() => add(product, qty, selectedVariant)}
                disabled={activeStock <= 0}
                data-testid="shop-add-to-cart-button"
                className="rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white h-12 px-6"
              >
                <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
              </Button>
              <Button onClick={buy} disabled={loading || activeStock <= 0} data-testid="shop-buy-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Buy now — ${(Number(activePrice) * qty).toFixed(2)}</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Frequently Bought Together */}
        {related.length > 0 && (
          <div className="mt-14 pt-8 border-t border-[#E6E9EE]" data-testid="frequently-bought-together">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold inline-flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" /> Frequently Bought Together
                </div>
                <h2 className="font-headline text-2xl md:text-3xl text-[#17352D] mt-1">
                  Customers who bought this also grabbed
                </h2>
              </div>
              <Link
                to="/shop"
                className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#17352D] hover:text-[#1E4338]"
              >
                Keep shopping <ArrowLeft className="h-4 w-4 rotate-180" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/shop/${p.id}`}
                  data-testid={`fbt-item-${p.id}`}
                  className="group relative rounded-2xl overflow-hidden border border-[#E6E9EE] bg-white shadow-[0_10px_30px_rgba(11,18,32,0.06)] hover:shadow-[0_18px_44px_rgba(11,18,32,0.12)] transition-shadow duration-200"
                >
                  <div className="aspect-square bg-[#F2F7F5] overflow-hidden relative">
                    {p.image ? (
                      <img
                        src={thumb(p.image, 400)}
                        alt={p.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.04]"
                        onError={(e) => { if (p.image && e.currentTarget.src !== p.image) e.currentTarget.src = p.image; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <ShoppingBag className="h-8 w-8" />
                      </div>
                    )}
                    {p.is_cheapest && (
                      <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold shadow">
                        Cheapest
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 truncate">
                      {p.category || 'Shop'}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-[#17352D] line-clamp-2 leading-snug min-h-[2.5rem]">
                      {p.name}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-base font-headline text-[#17352D]">
                        ${Number(p.price).toFixed(2)}
                      </div>
                      <span className="text-[11px] font-semibold text-[#C8922F] group-hover:underline">
                        View
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
